import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api';
import { useAuth, useLocale } from '../../context';
import { useSiteContent } from '../../context/SiteContentContext';
import { CASINO_LOBBIES, casinoRouteForLobby } from '../../data/casinoData';
import { useBottomSheetGestures } from '../../hooks/useBottomSheetGestures';
import { useCasinoGameFeed } from '../../hooks/useCasinoGameFeed';
import { lockScroll, unlockScroll } from '../../scrollLock';
import { clearBrowseHistory, readBrowseHistory } from '../../utils/browseHistory';
import CasinoSideGameCard from '../casino/CasinoSideGameCard';
import { IconSearch } from '../casino/CasinoGameIcons';
import { IconAccountCircleFilled, IconChevronRight } from '../VurgunIcons';
import { staticAsset } from '../../data/staticAssets.js';
import {
  IconTabHome,
  IconTabSport,
  IconTabCasino,
  IconTabPromos,
  IconTabHelp,
  IconTabMenu,
  IconTabResults,
  IconTabBonuses,
  IconTabTournaments,
  IconNavTv,
  IconNavVirtual,
  IconNavFast,
  IconTimeRemind,
  IconSheetClose,
  IconTrashMenu,
} from './MobileIcons';

const PANE_IDS = ['history', 'menu', 'sport', 'casino'];
const MENU_TABS = [
  { id: 'menu', labelKey: 'mobile.menu', pane: 1 },
  { id: 'sport', labelKey: 'nav.sport', pane: 2 },
  { id: 'casino', labelKey: 'nav.casino', pane: 3 },
];

const HISTORY_ICONS = {
  home: IconTabHome,
  casino: IconTabCasino,
  sport: IconTabSport,
  promos: IconTabPromos,
  help: IconTabHelp,
  account: IconTabMenu,
};

const LOBBY_ICON = {
  slots: IconTabCasino,
  live: IconTabCasino,
  poker: IconTabCasino,
  tv: IconNavTv,
  fast: IconNavFast,
  virtual: IconNavVirtual,
};

const LOBBY_ICON_SRC = {
  slots: '/static/content/Casino/categories/gr8/lobby-slots.png',
  live: '/static/content/Casino/categories/gr8/lobby-live.png',
  poker: '/static/content/Casino/categories/gr8/lobby-poker.png',
  tv: '/static/content/Casino/categories/gr8/lobby-tv.png',
  fast: '/static/content/Casino/categories/gr8/lobby-fast.png',
  virtual: '/static/content/Casino/categories/gr8/lobby-virtual.png',
};

const RECOMMENDED_ICON = staticAsset('/content/Casino/categories/gr8/tab-top.png');

function IconHistory() {
  return <IconTimeRemind />;
}

function HeaderTab({ active, icon, label, iconOnly, onClick, ariaLabel, ariaSelected }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={ariaSelected}
      aria-label={ariaLabel}
      className={`mobile-menu-sheet__tab${iconOnly ? ' mobile-menu-sheet__tab--icon' : ''}${active ? ' is-active' : ''}`}
      onClick={onClick}
    >
      {icon ? (
        <span className="mobile-menu-sheet__tab-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {label ? <span className="mobile-menu-sheet__tab-label">{label}</span> : null}
    </button>
  );
}

function MenuPaneLayout({ promo, children, footer }) {
  return (
    <div className="mobile-menu-sheet__pane-scroll">
      <div className="mobile-menu-sheet__pane-theme">
        <div className="mobile-menu-sheet__pane-main">
          {promo}
          <div className="mobile-menu-sheet__pane-content">{children}</div>
        </div>
        {footer ? <div className="mobile-menu-sheet__bottom">{footer}</div> : null}
      </div>
    </div>
  );
}

function IconPromoArrow() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return <IconTrashMenu />;
}


function userDisplayName(user) {
  const parts = [user?.firstName || user?.first_name, user?.lastName || user?.last_name]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
  if (parts.length) return parts.join(' ');
  return String(user?.name || user?.username || user?.email || '').trim() || '—';
}

function userAccountNumber(user) {
  return user?.accountNumber || user?.account_number || user?.id || '—';
}

function IconServiceChat() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <circle cx="20" cy="20" r="20" fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d="M20.0725 7.50028C15.6829 7.48264 11.6069 9.75874 9.32574 13.5001C7.04426 17.242 6.8911 21.9033 8.92207 25.7864L9.15184 26.2344C9.23969 26.399 9.25663 26.5766 9.20573 26.7408C8.87755 27.6081 8.5892 28.5446 8.35912 29.4962L8.33469 29.7011C8.33469 30.6579 8.8452 31.3153 9.85839 31.2928L10.0271 31.2725C10.9475 31.0693 11.8539 30.8074 12.7408 30.4886C12.8583 30.4588 13.0509 30.4704 13.2246 30.5415L14.0778 31.0243C14.0797 31.0302 14.0812 31.0347 14.0901 31.0399L14.1474 31.06C18.7412 33.4754 24.3515 32.8091 28.2496 29.3848C32.1483 25.9601 33.5249 20.4876 31.7094 15.6298C29.8941 10.7725 25.2644 7.52493 20.0725 7.50028ZM15.25 16.75C15.25 16.0596 15.8096 15.5 16.5 15.5H23.5C24.1904 15.5 24.75 16.0596 24.75 16.75C24.75 17.4404 24.1904 18 23.5 18H16.5C15.8096 18 15.25 17.4404 15.25 16.75ZM16.5 19.5C15.8096 19.5 15.25 20.0596 15.25 20.75C15.25 21.4404 15.8096 22 16.5 22H21.5C22.1904 22 22.75 21.4404 22.75 20.75C22.75 20.0596 22.1904 19.5 21.5 19.5H16.5Z" fill="#fff" />
    </svg>
  );
}

function MenuInfoPanel({ t: tr, user, onOpen }) {
  const name = userDisplayName(user);
  const accountNo = userAccountNumber(user);
  return (
    <div className="mobile-menu-sheet__info-panel">
      <button type="button" className="mobile-menu-sheet__info-panel-btn" onClick={onOpen}>
        <span className="mobile-menu-sheet__info-panel-icon" aria-hidden="true">
          <IconAccountCircleFilled />
        </span>
        <span className="mobile-menu-sheet__info-panel-copy">
          <span className="mobile-menu-sheet__info-panel-name">{name}</span>
          <span className="mobile-menu-sheet__info-panel-meta">
            {tr('account.profile.field.accountNo')} {accountNo}
          </span>
        </span>
        <span className="mobile-menu-sheet__info-panel-chevron" aria-hidden="true">
          <IconChevronRight />
        </span>
      </button>
    </div>
  );
}

function MenuLogoutButton({ t: tr, onClick }) {
  return (
    <button type="button" className="mobile-menu-sheet__logout" onClick={onClick}>
      {tr('header.logout')}
    </button>
  );
}

function MenuBottomFooter({ t: tr, onOpenChat }) {
  return (
    <div className="mobile-menu-sheet__bottom-panel">
      <button
        type="button"
        className="mobile-menu-sheet__chat"
        onClick={onOpenChat}
        aria-label={tr('header.help')}
      >
        <IconServiceChat />
      </button>
    </div>
  );
}

function stripHtml(value = '') {
  return String(value).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function GuestLoginBlock({t: tr, onLogin}) {
  return (
    <div className="mobile-menu-sheet__login">
      <div className="mobile-menu-sheet__login-title">{tr('mobile.menu.loginTitle')}</div>
      <div className="mobile-menu-sheet__login-text">{tr('mobile.menu.loginText')}</div>
      <button type="button" className="mobile-menu-sheet__login-btn" onClick={onLogin}>
        {tr('header.login')}
      </button>
    </div>
  );
}

function IconGiftSolid() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 8.5V17.5M10 8.5H4.5C3.67157 8.5 3 9.17157 3 10V16.5C3 17.0523 3.44772 17.5 4 17.5H16C16.5523 17.5 17 17.0523 17 16.5V10C17 9.17157 16.3284 8.5 15.5 8.5H10Z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.5 8.5H16.5V7C16.5 6.17157 15.8284 5.5 15 5.5H5C4.17157 5.5 3.5 6.17157 3.5 7V8.5Z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 5.5C10 5.5 10 3.5 8.25 2.75C6.5 2 5.5 3.5 6.25 4.75C7 6 10 5.5 10 5.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M10 5.5C10 5.5 10 3.5 11.75 2.75C13.5 2 14.5 3.5 13.75 4.75C13 6 10 5.5 10 5.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function PromoRail({banners, onOpen, onPromos, t: tr}) {
  const list = (banners || []).slice(0, 12);

  return (
    <div className="mobile-menu-sheet__promo-rail">
      <div className="mobile-menu-sheet__promos" data-swipe-horizontal="true">
        {list.map((banner) => {
          const title = stripHtml(banner.title || '');
          return (
            <button
              key={banner.id || title}
              type="button"
              className="mobile-menu-sheet__promo"
              onClick={() => onOpen(banner)}
              style={banner.bg ? { backgroundColor: banner.bg } : undefined}
            >
              <div className="mobile-menu-sheet__promo-content">
                <div className="mobile-menu-sheet__promo-header">
                  <span className="mobile-menu-sheet__promo-badge">{banner.badge || tr('nav.promos')}</span>
                  <span className="mobile-menu-sheet__promo-title">{title}</span>
                </div>
                <span className="mobile-menu-sheet__promo-arrow" aria-hidden="true">
                  <IconPromoArrow />
                </span>
              </div>
              {banner.imageUrl ? (
                <img className="mobile-menu-sheet__promo-image" src={banner.imageUrl} alt="" loading="lazy" />
              ) : null}
            </button>
          );
        })}
        <div className="mobile-menu-sheet__promo-actions">
          <button
            type="button"
            className="mobile-menu-sheet__promo-action"
            onClick={onPromos}
            aria-label={tr('nav.promos')}
          >
            <IconGiftSolid />
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkRow({ title, note, icon: Icon, onClick }) {
  return (
    <button type="button" className={`mobile-menu-sheet__link${note ? ' mobile-menu-sheet__link--noted' : ''}`} onClick={onClick}>
      {Icon ? (
        <span className="mobile-menu-sheet__link-icon" aria-hidden="true">
          <Icon />
        </span>
      ) : null}
      <span className="mobile-menu-sheet__link-copy">
        <span className="mobile-menu-sheet__link-title">{title}</span>
        {note ? <span className="mobile-menu-sheet__link-note">{note}</span> : null}
      </span>
      <span className="mobile-menu-sheet__link-chevron" aria-hidden="true">
        <IconChevronRight />
      </span>
    </button>
  );
}

function LinkGroup({ children }) {
  return <div className="mobile-menu-sheet__group">{children}</div>;
}

function MenuMainLinks({t: tr, onNavigate, onClose}) {
  const go = (pageId, opts) => () => {
    onClose();
    onNavigate(pageId, opts);
  };

  return (
    <>
      <LinkGroup>
        <LinkRow icon={IconTabSport} title={tr('nav.sport')} onClick={go('sport')} />
        <LinkRow icon={IconTabResults} title={tr('nav.results')} onClick={go('home')} />
      </LinkGroup>
      <LinkGroup>
        <LinkRow icon={IconTabBonuses} title={tr('nav.promos')} onClick={go('promos')} />
        <LinkRow icon={IconTabTournaments} title={tr('mobile.menu.tournaments')} onClick={go('promos')} />
      </LinkGroup>
      <LinkGroup>
        <LinkRow icon={IconTabCasino} title={tr('nav.casino')} onClick={go('casino', { ...casinoRouteForLobby('slots'), casinoProvider: null, casinoQuery: null })} />
        <LinkRow icon={IconNavTv} title={tr('nav.vurgunTv') || 'Vurgun TV'} onClick={go('vurgunTv')} />
        <LinkRow icon={IconNavVirtual} title={tr('nav.virtual')} onClick={go('casino', { ...casinoRouteForLobby('virtual'), casinoProvider: null, casinoQuery: null })} />
        <LinkRow icon={IconNavFast} title={tr('nav.fast')} onClick={go('casino', { ...casinoRouteForLobby('fast'), casinoProvider: null, casinoQuery: null })} />
      </LinkGroup>
      <LinkGroup>
        <LinkRow title={tr('header.help')} note={tr('mobile.menu.helpNote')} onClick={go('help')} />
        <LinkRow title={tr('nav.apps')} note={tr('mobile.menu.appsNote')} onClick={go('home')} />
        <LinkRow title={tr('mobile.menu.about')} note={tr('mobile.menu.aboutNote')} onClick={go('help')} />
        <LinkRow title={tr('basinda.title')} note={tr('basinda.menuNote')} onClick={go('basindaBiz')} />
      </LinkGroup>
    </>
  );
}

function MenuGuestBody({t: tr, banners, onNavigate, onClose, onLogin, onBanner, onPromos, onOpenChat}) {
  return (
    <MenuPaneLayout
      promo={<PromoRail banners={banners} onOpen={onBanner} onPromos={onPromos} t={tr} />}
      footer={<MenuBottomFooter t={tr} onOpenChat={onOpenChat} />}
    >
      <GuestLoginBlock t={tr} onLogin={() => { onClose(); onLogin(); }} />
      <MenuMainLinks t={tr} onNavigate={onNavigate} onClose={onClose} />
    </MenuPaneLayout>
  );
}

function MenuUserBody({t: tr, user, banners, onNavigate, onClose, onBanner, onPromos, onOpenAccount, onLogout, onOpenChat}) {
  const openAccount = () => {
    onClose();
    if (onOpenAccount) {
      onOpenAccount();
      return;
    }
    onNavigate('account');
  };

  return (
    <MenuPaneLayout
      promo={<PromoRail banners={banners} onOpen={onBanner} onPromos={onPromos} t={tr} />}
      footer={<MenuBottomFooter t={tr} onOpenChat={onOpenChat} />}
    >
      <MenuInfoPanel t={tr} user={user} onOpen={openAccount} />
      <MenuMainLinks t={tr} onNavigate={onNavigate} onClose={onClose} />
      <MenuLogoutButton
        t={tr}
        onClick={() => {
          onClose();
          onLogout?.();
        }}
      />
    </MenuPaneLayout>
  );
}

const SPORT_FAV_KEY = 'vb-sport-nav-favs';
const SPORT_ICON_BASE = 'https://nexustrike.com/images/sports';

const POPULAR_LEAGUE_RE = [
  /^uefa\s+şampiyonlar\s+ligi$/i,
  /^uefa\s+champions\s+league$/i,
  /^la\s+liga$/i,
  /^spain\.?\s*primera/i,
  /^england\.?\s*premier\s+league$/i,
  /^premier\s+lig$/i,
  /^süper\s+lig$/i,
  /^super\s+lig$/i,
  /^uefa\s+avrupa\s+ligi$/i,
  /^uefa\s+europa\s+league$/i,
  /^uefa\s+konferans\s+ligi$/i,
  /^uefa\s+conference\s+league$/i,
  /^serie\s+a$/i,
  /^italy\.?\s*serie\s+a$/i,
  /^bundesliga$/i,
  /^germany\.?\s*bundesliga$/i,
  /^ligue\s+1$/i,
  /^nba$/i,
  /^euroleague$/i,
  /^atp\./i,
  /^wta\./i,
  /^us\s+open/i,
  /^wimbledon/i,
];

const POPULAR_EXCLUDE_RE = /özel\s*bahis|special\s*bets|istatistik|statistics|takım\s*vs|fc\s*\d+|cb\s*\d+|nba\s*2k|cyber|handball\s*\d+|fifa\s*\d+|virtual|sanal|serbest\s*pyramit|winner|çiftler|doubles|ana\s*yol/i;

function readSportFavs() {
  try {
    const raw = JSON.parse(localStorage.getItem(SPORT_FAV_KEY) || '[]');
    return Array.isArray(raw) ? raw.map(Number).filter(Number.isFinite) : [];
  } catch {
    return [];
  }
}

function writeSportFavs(ids) {
  try {
    localStorage.setItem(SPORT_FAV_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function sportIconUrl(sportId) {
  const id = Number(sportId);
  return id ? `${SPORT_ICON_BASE}/${id}.png` : '';
}

function pickMenuLists(menu, filter) {
  if (!menu) {
    return { sports: [], countries: [], championships: [] };
  }
  if (filter === 'live') {
    return {
      sports: menu.liveSports || [],
      countries: menu.liveCountries || [],
      championships: menu.liveChampionships || [],
    };
  }
  if (filter === 'line') {
    return {
      sports: menu.lineSports || [],
      countries: menu.lineCountries || [],
      championships: menu.lineChampionships || [],
    };
  }
  return {
    sports: menu.totalSports || [],
    countries: menu.totalCountries || [],
    championships: menu.totalChampionships || [],
  };
}

function popularChampionships(championships, limit = 12) {
  const list = Array.isArray(championships) ? championships : [];
  const scored = list
    .map((c) => {
      const name = String(c.name || '').trim();
      if (!name || POPULAR_EXCLUDE_RE.test(name)) return null;
      let boost = 0;
      POPULAR_LEAGUE_RE.forEach((re, i) => {
        if (re.test(name)) boost = Math.max(boost, (POPULAR_LEAGUE_RE.length - i) * 100);
      });
      if (!boost) return null;
      const sportBoost = Number(c.sportId) === 1 ? 50 : 0;
      return { ...c, _score: boost + sportBoost + Math.min(Number(c.counter) || 0, 40) };
    })
    .filter(Boolean)
    .sort((a, b) => b._score - a._score);
  const seen = new Set();
  const out = [];
  for (const c of scored) {
    const key = String(c.name || '').toLowerCase().replace(/\s+/g, ' ');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
    if (out.length >= limit) break;
  }
  return out;
}

function IconStar({ filled }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M9 1.8 10.95 6.3l4.9.45-3.7 3.25 1.1 4.8L9 12.6l-4.25 2.2 1.1-4.8-3.7-3.25 4.9-.45L9 1.8Z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCompass() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M9.6 4.4 7.7 7.7 4.4 9.6 6.3 6.3 9.6 4.4Z" fill="currentColor" />
    </svg>
  );
}

function IconFire() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none" aria-hidden="true">
      <path d="M6.2 1.2c.2 1.4-.5 2.2-1.3 3.1C3.8 5.4 2.8 6.5 2.8 8.2c0 2 1.5 3.4 3.4 3.4s3.4-1.4 3.4-3.4c0-1.5-.7-2.4-1.6-3.5-.6-.7-1.2-1.5-1.2-2.7V1.2Z" fill="currentColor" />
    </svg>
  );
}

function IconTimer() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7.5" r="5.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M7 4.8v3.1l2 1.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M5.2 1.4h3.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function IconClearX() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SportTabBody({ t: tr, locale, onNavigate, onClose, enabled }) {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  const [favs, setFavs] = useState(() => (typeof window !== 'undefined' ? readSportFavs() : []));
  const [view, setView] = useState({ mode: 'root' });
  const [dateOpen, setDateOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query), 220);
    return () => window.clearTimeout(id);
  }, [query]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    api.nexusSportsMenu({ lang: locale === 'tr' ? 'tr' : 'en', fresh: true })
      .then((data) => {
        if (!cancelled && data) setMenu(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enabled, locale]);

  useEffect(() => {
    if (!enabled) {
      setSearchActive(false);
      setQuery('');
      setDebounced('');
      setDateOpen(false);
      setView({ mode: 'root' });
    }
  }, [enabled]);

  const lists = pickMenuLists(menu, filter);
  const eventType = filter === 'live' ? 'live' : 'line';
  const q = debounced.trim().toLocaleLowerCase(locale === 'tr' ? 'tr-TR' : 'en-US');

  const sortedSports = useMemo(() => {
    const favSet = new Set(favs);
    let rows = [...(lists.sports || [])];
    if (favOnly) rows = rows.filter((s) => favSet.has(Number(s.sportId)));
    rows.sort((a, b) => {
      const af = favSet.has(Number(a.sportId)) ? 1 : 0;
      const bf = favSet.has(Number(b.sportId)) ? 1 : 0;
      if (af !== bf) return bf - af;
      return (Number(b.counter) || 0) - (Number(a.counter) || 0);
    });
    return rows;
  }, [lists.sports, favs, favOnly]);

  const chips = useMemo(() => popularChampionships(lists.championships, 12), [lists.championships]);

  const activeSport = view.mode === 'sport' || view.mode === 'country'
    ? sortedSports.find((s) => Number(s.sportId) === Number(view.sportId))
      || lists.sports.find((s) => Number(s.sportId) === Number(view.sportId))
    : null;

  const sportCountries = useMemo(() => {
    if (!activeSport) return [];
    return (lists.countries || [])
      .filter((c) => Number(c.sportId) === Number(activeSport.sportId))
      .sort((a, b) => (Number(b.counter) || 0) - (Number(a.counter) || 0));
  }, [lists.countries, activeSport]);

  const sportPopular = useMemo(() => {
    if (!activeSport) return [];
    return popularChampionships(
      (lists.championships || []).filter((c) => Number(c.sportId) === Number(activeSport.sportId)),
      8,
    );
  }, [lists.championships, activeSport]);

  const countryChamps = useMemo(() => {
    if (view.mode !== 'country') return [];
    return (lists.championships || [])
      .filter((c) => Number(c.sportId) === Number(view.sportId) && Number(c.countryId) === Number(view.countryId))
      .sort((a, b) => (Number(b.counter) || 0) - (Number(a.counter) || 0));
  }, [lists.championships, view]);

  const searchHits = useMemo(() => {
    if (!q) return { sports: [], championships: [] };
    const sports = (lists.sports || []).filter((s) => String(s.sportName || '').toLocaleLowerCase(locale === 'tr' ? 'tr-TR' : 'en-US').includes(q));
    const championships = (lists.championships || [])
      .filter((c) => String(c.name || '').toLocaleLowerCase(locale === 'tr' ? 'tr-TR' : 'en-US').includes(q))
      .slice(0, 40);
    return { sports, championships };
  }, [q, lists.sports, lists.championships, locale]);

  const goSport = (opts = {}) => {
    onClose();
    onNavigate('sport', {
      sportFilter: filter === 'all' ? 'all' : filter,
      ...opts,
    });
  };

  const openLeague = (league) => {
    if (!league) return;
    goSport({
      sportEvent: {
        sportId: Number(league.sportId),
        leagueId: Number(league.id),
        type: eventType,
      },
    });
  };

  const openSportPage = (sport) => {
    if (!sport) return;
    goSport({
      sportEvent: {
        sportId: Number(sport.sportId),
        type: eventType,
      },
    });
  };

  const toggleFav = (sportId, e) => {
    e?.stopPropagation?.();
    const id = Number(sportId);
    setFavs((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      writeSportFavs(next);
      return next;
    });
  };

  const openSearch = () => {
    setSearchActive(true);
    setDateOpen(false);
    const pane = searchInputRef.current?.closest('.mobile-menu-sheet__pane-scroll');
    if (pane) pane.scrollTop = 0;
  };

  const cancelSearch = () => {
    setSearchActive(false);
    setQuery('');
    setDebounced('');
    searchInputRef.current?.blur();
  };

  const filterTabs = [
    { id: 'live', label: tr('sport.filter.live') },
    { id: 'line', label: tr('sport.filter.prematch') },
    { id: 'all', label: tr('sport.filter.all') },
  ];

  const sportsHeading = String(tr('nav.sport') || 'Sports').toLocaleUpperCase(locale === 'tr' ? 'tr-TR' : 'en-US');
  const popularHeading = locale === 'tr' ? 'POPÜLER' : 'POPULAR';
  const allSportsLabel = locale === 'tr' ? 'Tüm sporlar' : 'All sports';

  return (
    <MenuPaneLayout footer={null}>
      <div className={`mobile-menu-sheet__sport-nav${searchActive ? ' is-searching' : ''}`}>
        <div className="mobile-menu-sheet__sport-sticky">
          <div className="mobile-menu-sheet__sport-search">
            <div className="mobile-menu-sheet__sport-search-row">
              <label className="mobile-menu-sheet__sport-search-box">
                <IconSearch />
                <input
                  ref={searchInputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={openSearch}
                  placeholder={tr('home.sport.searchEvents')}
                  enterKeyHint="search"
                />
                {query ? (
                  <button
                    type="button"
                    className="mobile-menu-sheet__sport-search-clear"
                    onClick={() => { setQuery(''); searchInputRef.current?.focus(); }}
                    aria-label="Clear"
                  >
                    <IconClearX />
                  </button>
                ) : null}
              </label>
              {searchActive ? (
                <button type="button" className="mobile-menu-sheet__sport-search-cancel" onClick={cancelSearch}>
                  {locale === 'tr' ? 'İptal' : 'Cancel'}
                </button>
              ) : null}
            </div>
          </div>

          {!searchActive ? (
            <div className="mobile-menu-sheet__sport-filter">
              <div className="mobile-menu-sheet__sport-tabs" role="tablist">
                {filterTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={filter === tab.id}
                    className={`mobile-menu-sheet__sport-tab${filter === tab.id ? ' is-active' : ''}`}
                    onClick={() => { setFilter(tab.id); setView({ mode: 'root' }); }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="mobile-menu-sheet__sport-filter-actions">
                <div className="mobile-menu-sheet__sport-date-wrap">
                  <button
                    type="button"
                    className={`mobile-menu-sheet__sport-date${dateOpen ? ' is-open' : ''}`}
                    aria-label={tr('sport.filter.allEvents')}
                    onClick={() => setDateOpen((v) => !v)}
                  >
                    <IconTimer />
                  </button>
                  {dateOpen ? (
                    <div className="mobile-menu-sheet__sport-date-menu">
                      <button
                        type="button"
                        onClick={() => setDateOpen(false)}
                      >
                        {tr('sport.filter.allEvents')}
                      </button>
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={`mobile-menu-sheet__sport-fav-switch${favOnly ? ' is-on' : ''}`}
                  aria-pressed={favOnly}
                  aria-label={tr('nav.sport')}
                  onClick={() => setFavOnly((v) => !v)}
                >
                  <span className="mobile-menu-sheet__sport-fav-switch-thumb">
                    <IconStar filled={favOnly} />
                  </span>
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {searchActive ? (
          <div className="mobile-menu-sheet__sport-list">
            {!q ? (
              <div className="mobile-menu-sheet__sport-empty">{tr('home.sport.searchEvents')}</div>
            ) : (
              <>
                {searchHits.sports.map((sport) => (
                  <button
                    key={`s-${sport.sportId}`}
                    type="button"
                    className="mobile-menu-sheet__sport-row"
                    onClick={() => openSportPage(sport)}
                  >
                    <img className="mobile-menu-sheet__sport-row-icon" src={sportIconUrl(sport.sportId)} alt="" loading="lazy" />
                    <span className="mobile-menu-sheet__sport-row-title">{sport.sportName}</span>
                    <span className="mobile-menu-sheet__sport-row-count">{sport.counter}</span>
                    <span className="mobile-menu-sheet__sport-row-chevron" aria-hidden="true"><IconChevronRight /></span>
                  </button>
                ))}
                {searchHits.championships.map((league) => (
                  <button
                    key={`c-${league.id}`}
                    type="button"
                    className="mobile-menu-sheet__sport-row mobile-menu-sheet__sport-row--league"
                    onClick={() => openLeague(league)}
                  >
                    <span className="mobile-menu-sheet__sport-row-title">{league.name}</span>
                    <span className="mobile-menu-sheet__sport-row-count">{league.counter}</span>
                    <span className="mobile-menu-sheet__sport-row-chevron" aria-hidden="true"><IconChevronRight /></span>
                  </button>
                ))}
                {!searchHits.sports.length && !searchHits.championships.length ? (
                  <div className="mobile-menu-sheet__sport-empty">{locale === 'tr' ? 'Sonuç yok' : 'No results'}</div>
                ) : null}
              </>
            )}
          </div>
        ) : view.mode === 'root' ? (
          <div className="mobile-menu-sheet__sport-list">
            {chips.length ? (
              <div className="mobile-menu-sheet__sport-chips" data-swipe-horizontal="true">
                {chips.map((league) => (
                  <button
                    key={league.id}
                    type="button"
                    className="mobile-menu-sheet__sport-chip"
                    onClick={() => openLeague(league)}
                  >
                    {league.name}
                  </button>
                ))}
              </div>
            ) : null}

            <button
              type="button"
              className="mobile-menu-sheet__sport-source mobile-menu-sheet__sport-source--top"
              onClick={() => goSport()}
            >
              <span className="mobile-menu-sheet__sport-source-icon"><IconCompass /></span>
              <span className="mobile-menu-sheet__sport-source-title">{tr('sport.filter.all')}</span>
              <span className="mobile-menu-sheet__sport-source-chevron" aria-hidden="true"><IconChevronRight /></span>
            </button>
            <button
              type="button"
              className="mobile-menu-sheet__sport-source mobile-menu-sheet__sport-source--bottom"
              onClick={() => goSport()}
            >
              <span className="mobile-menu-sheet__sport-source-icon"><IconFire /></span>
              <span className="mobile-menu-sheet__sport-source-title">{locale === 'tr' ? 'Top' : 'Top'}</span>
              <span className="mobile-menu-sheet__sport-source-chevron" aria-hidden="true"><IconChevronRight /></span>
            </button>

            <div className="mobile-menu-sheet__sport-section">{sportsHeading}</div>

            {loading && !sortedSports.length ? (
              <div className="mobile-menu-sheet__sport-empty">…</div>
            ) : null}

            {sortedSports.map((sport, idx) => {
              const id = Number(sport.sportId);
              const isFav = favs.includes(id);
              return (
                <div
                  key={id}
                  className={`mobile-menu-sheet__sport-row-wrap${idx === 0 ? ' is-first' : ''}${idx === sortedSports.length - 1 ? ' is-last' : ''}`}
                >
                  <button
                    type="button"
                    className={`mobile-menu-sheet__sport-fav-btn${isFav ? ' is-on' : ''}`}
                    onClick={(e) => toggleFav(id, e)}
                    aria-label="Favorite"
                  >
                    <IconStar filled={isFav} />
                  </button>
                  <button
                    type="button"
                    className="mobile-menu-sheet__sport-row"
                    onClick={() => setView({ mode: 'sport', sportId: id })}
                  >
                    <img className="mobile-menu-sheet__sport-row-icon" src={sportIconUrl(id)} alt="" loading="lazy" />
                    <span className="mobile-menu-sheet__sport-row-title">{sport.sportName}</span>
                    <span className="mobile-menu-sheet__sport-row-chevron" aria-hidden="true"><IconChevronRight /></span>
                  </button>
                </div>
              );
            })}
          </div>
        ) : view.mode === 'sport' && activeSport ? (
          <div className="mobile-menu-sheet__sport-list">
            <button
              type="button"
              className="mobile-menu-sheet__sport-crumb"
              onClick={() => setView({ mode: 'root' })}
            >
              <span className="mobile-menu-sheet__sport-crumb-arrow" aria-hidden="true">‹</span>
              {allSportsLabel}
            </button>
            <button
              type="button"
              className="mobile-menu-sheet__sport-row mobile-menu-sheet__sport-row--current"
              onClick={() => openSportPage(activeSport)}
            >
              <img className="mobile-menu-sheet__sport-row-icon" src={sportIconUrl(activeSport.sportId)} alt="" loading="lazy" />
              <span className="mobile-menu-sheet__sport-row-title">{activeSport.sportName}</span>
              <span className="mobile-menu-sheet__sport-row-count">{activeSport.counter}</span>
              <span className="mobile-menu-sheet__sport-row-chevron" aria-hidden="true"><IconChevronRight /></span>
            </button>
            {sportPopular.length ? (
              <>
                <div className="mobile-menu-sheet__sport-section">{popularHeading}</div>
                {sportPopular.map((league) => (
                  <button
                    key={league.id}
                    type="button"
                    className="mobile-menu-sheet__sport-row mobile-menu-sheet__sport-row--league"
                    onClick={() => openLeague(league)}
                  >
                    <span className="mobile-menu-sheet__sport-row-title">{league.name}</span>
                    <span className="mobile-menu-sheet__sport-row-count">{league.counter}</span>
                    <span className="mobile-menu-sheet__sport-row-chevron" aria-hidden="true"><IconChevronRight /></span>
                  </button>
                ))}
              </>
            ) : null}
            {sportCountries.map((country, idx) => (
              <button
                key={country.id}
                type="button"
                className={`mobile-menu-sheet__sport-row mobile-menu-sheet__sport-row--league${idx === 0 ? ' is-round-top' : ''}`}
                onClick={() => setView({ mode: 'country', sportId: Number(activeSport.sportId), countryId: Number(country.id), countryName: country.name })}
              >
                <span className="mobile-menu-sheet__sport-row-title">{country.name}</span>
                <span className="mobile-menu-sheet__sport-row-count">{country.counter}</span>
                <span className="mobile-menu-sheet__sport-row-chevron" aria-hidden="true"><IconChevronRight /></span>
              </button>
            ))}
          </div>
        ) : view.mode === 'country' && activeSport ? (
          <div className="mobile-menu-sheet__sport-list">
            <button
              type="button"
              className="mobile-menu-sheet__sport-crumb"
              onClick={() => setView({ mode: 'sport', sportId: Number(view.sportId) })}
            >
              <span className="mobile-menu-sheet__sport-crumb-arrow" aria-hidden="true">‹</span>
              {activeSport.sportName}
            </button>
            <div className="mobile-menu-sheet__sport-section">{view.countryName || ''}</div>
            {countryChamps.map((league) => (
              <button
                key={league.id}
                type="button"
                className="mobile-menu-sheet__sport-row mobile-menu-sheet__sport-row--league"
                onClick={() => openLeague(league)}
              >
                <span className="mobile-menu-sheet__sport-row-title">{league.name}</span>
                <span className="mobile-menu-sheet__sport-row-count">{league.counter}</span>
                <span className="mobile-menu-sheet__sport-row-chevron" aria-hidden="true"><IconChevronRight /></span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </MenuPaneLayout>
  );
}

function formatGamesCount(n, locale) {
  if (n == null || !Number.isFinite(Number(n))) return '...';
  const num = Number(n);
  const formatted = locale === 'tr' ? num.toLocaleString('tr-TR') : num.toLocaleString('en-US');
  return locale === 'tr' ? `${formatted} oyun` : `${formatted} games`;
}

function recommendedPageSize(cols = 2) {
  if (typeof window === 'undefined') return 36;
  const h = Math.max(window.innerHeight || 800, 600);
  const rowH = 170;
  const rows = Math.ceil(h / rowH) + 3;
  return Math.max(36, Math.min(96, rows * cols));
}

function CasinoTabBody({t: tr, locale, banners, onNavigate, onClose, onBanner, onPromos, enabled}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [stats, setStats] = useState(null);
  const [pageSize] = useState(() => recommendedPageSize(2));
  const loadMoreRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query), 280);
    return () => window.clearTimeout(id);
  }, [query]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    api.casinoStats()
      .then((data) => { if (!cancelled && data) setStats(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setSearchActive(false);
      setQuery('');
      setDebounced('');
    }
  }, [enabled]);

  const isFiltered = Boolean(debounced.trim());
  const feed = useCasinoGameFeed({
    category: isFiltered ? 'all' : 'recommended',
    lobby: 'slots',
    q: debounced,
    sort: 'popular',
    pageSize,
    enabled,
  });

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !enabled || !feed.hasMore || feed.loading || feed.loadingMore) return undefined;
    const root = node.closest('.mobile-menu-sheet__pane-scroll');
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) feed.loadMore();
    }, { root: root || null, rootMargin: '360px 0px', threshold: 0 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, feed.hasMore, feed.loading, feed.loadingMore, feed.loadMore, feed.games.length]);

  useEffect(() => {
    if (!enabled || !feed.hasMore || feed.loading || feed.loadingMore) return;
    const node = loadMoreRef.current;
    const root = node?.closest('.mobile-menu-sheet__pane-scroll');
    if (!root) return;
    if (root.scrollHeight <= root.clientHeight + 48) {
      feed.loadMore();
    }
  }, [enabled, feed.hasMore, feed.loading, feed.loadingMore, feed.loadMore, feed.games.length]);

  const go = (pageId, opts) => () => {
    onClose();
    onNavigate(pageId, opts);
  };

  const lobbyLabel = (id) => {
    if (id === 'slots') return tr('casino.lobby.slots');
    if (id === 'live') return tr('casino.lobby.live');
    if (id === 'poker') return tr('casino.lobby.poker');
    if (id === 'tv') return tr('casino.lobby.tv');
    if (id === 'fast') return tr('casino.lobby.fast');
    if (id === 'virtual') return tr('casino.lobby.virtual');
    return id;
  };

  const openSearch = () => {
    setSearchActive(true);
    const pane = searchInputRef.current?.closest('.mobile-menu-sheet__pane-scroll');
    if (pane) pane.scrollTop = 0;
  };

  const cancelSearch = () => {
    setSearchActive(false);
    setQuery('');
    setDebounced('');
    searchInputRef.current?.blur();
  };

  const clearQuery = () => {
    setQuery('');
    searchInputRef.current?.focus();
  };

  const sectionLabel = isFiltered
    ? `${String(tr('casino.side.games') || 'Games').toLocaleUpperCase(locale === 'tr' ? 'tr-TR' : 'en-US')}\u00A0(${feed.loading && !feed.games.length ? '...' : (Number(feed.total) || 0)})`
    : tr('casino.side.recommended');

  return (
    <MenuPaneLayout
      promo={searchActive ? null : <PromoRail banners={banners} onOpen={onBanner} onPromos={onPromos} t={tr} />}
      footer={null}
    >
      <div className={`mobile-menu-sheet__casino${searchActive ? ' is-searching' : ''}`}>
        <div className="mobile-menu-sheet__casino-search">
          <div className="mobile-menu-sheet__casino-search-row">
            <label className="mobile-menu-sheet__casino-search-box">
              <IconSearch />
              <input
                ref={searchInputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={openSearch}
                placeholder={tr('mobile.menu.searchGames')}
                autoComplete="off"
                spellCheck={false}
              />
              {query ? (
                <button
                  type="button"
                  className="mobile-menu-sheet__casino-search-clear"
                  onClick={clearQuery}
                  aria-label={tr('auth.verify.cancel')}
                >
                  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M3.728 3.727a.547.547 0 0 1 .774 0l3.499 3.507L11.5 3.727a.547.547 0 0 1 .773.773L8.766 8l3.508 3.498a.544.544 0 0 1 0 .773l-.001.002a.545.545 0 0 1-.772 0L8 8.765l-3.5 3.507a.545.545 0 0 1-.772 0h-.001a.543.543 0 0 1 0-.773l3.507-3.5L3.728 4.5a.547.547 0 0 1 0-.773Z"
                    />
                  </svg>
                </button>
              ) : null}
            </label>
            {searchActive ? (
              <button
                type="button"
                className="mobile-menu-sheet__casino-search-cancel"
                onClick={cancelSearch}
              >
                {tr('auth.verify.cancel')}
              </button>
            ) : null}
          </div>
        </div>
        {!searchActive ? (
          <div className="mobile-menu-sheet__casino-lobbies">
            {CASINO_LOBBIES.map((lobby) => {
              const Icon = LOBBY_ICON[lobby.id] || IconTabCasino;
              const iconSrc = LOBBY_ICON_SRC[lobby.id];
              const count = stats ? (stats[lobby.id] ?? 0) : null;
              return (
                <button
                  key={lobby.id}
                  type="button"
                  className="mobile-menu-sheet__casino-lobby"
                  onClick={go('casino', {
                    ...casinoRouteForLobby(lobby.id),
                    casinoProvider: null,
                    casinoQuery: null,
                  })}
                >
                  <span className="mobile-menu-sheet__casino-lobby-icon" aria-hidden="true">
                    {iconSrc ? (
                      <img src={iconSrc} alt="" width={28} height={28} />
                    ) : (
                      <Icon />
                    )}
                  </span>
                  <span className="mobile-menu-sheet__casino-lobby-copy">
                    <span className="mobile-menu-sheet__casino-lobby-title">{lobbyLabel(lobby.id)}</span>
                    <span className="mobile-menu-sheet__casino-lobby-sub">{formatGamesCount(count, locale)}</span>
                  </span>
                  <span className="mobile-menu-sheet__casino-lobby-chevron" aria-hidden="true">
                    <IconChevronRight />
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
        <div className="mobile-menu-sheet__casino-rec">
          <div className="mobile-menu-sheet__casino-rec-caption">
            {!isFiltered ? (
              <i
                className="mobile-menu-sheet__casino-rec-caption-icon"
                style={{ backgroundImage: `url(${RECOMMENDED_ICON})` }}
                aria-hidden="true"
              />
            ) : null}
            <span className="mobile-menu-sheet__casino-rec-caption-label">
              {sectionLabel}
            </span>
          </div>
          {feed.loading && !feed.games.length ? (
            <div className="mobile-menu-sheet__casino-rec-loading" aria-busy="true">
              <span className="mobile-menu-sheet__casino-rec-spinner" />
            </div>
          ) : !feed.games.length ? (
            <p className="mobile-menu-sheet__casino-rec-empty">{tr('casino.side.noGames')}</p>
          ) : (
            <div className="mobile-menu-sheet__casino-rec-grid">
              {feed.games.map((game, index) => (
                <CasinoSideGameCard
                  key={game.id ?? `${game.name}-${index}`}
                  game={game}
                  onNavigate={(page, opts) => {
                    onClose();
                    onNavigate(page, opts);
                  }}
                />
              ))}
            </div>
          )}
          {feed.hasMore ? (
            <div className="mobile-menu-sheet__casino-rec-more" ref={loadMoreRef} aria-hidden="true" />
          ) : null}
        </div>
      </div>
    </MenuPaneLayout>
  );
}

function HistoryBody({t: tr, items, onOpen, onClear}) {
  return (
    <div className="mobile-menu-sheet__pane-scroll mobile-menu-sheet__pane-scroll--history">
      <div className="mobile-menu-sheet__history-pane">
        <div className="mobile-menu-sheet__history-header">{tr('mobile.menu.history')}</div>
        <div className="mobile-menu-sheet__history-list">
          {!items.length ? (
            <div className="mobile-menu-sheet__history-empty">
              <div className="mobile-menu-sheet__history-empty-text">{tr('mobile.menu.historyEmptyText')}</div>
            </div>
          ) : (
            items.map((item, index) => {
              const Icon = HISTORY_ICONS[item.icon] || IconTabHome;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`mobile-menu-sheet__history-item${index === 0 ? ' is-current' : ''}`}
                  onClick={() => onOpen(item)}
                >
                  <span className="mobile-menu-sheet__history-item-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="mobile-menu-sheet__history-item-copy">
                    {item.crumb ? (
                      <span className="mobile-menu-sheet__history-item-crumb">{item.crumb}</span>
                    ) : null}
                    <span className="mobile-menu-sheet__history-item-label">{item.label}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
        {items.length ? (
          <div className="mobile-menu-sheet__history-bottom">
            <button
              type="button"
              className="mobile-menu-sheet__history-clear"
              onClick={onClear}
              aria-label={tr('mobile.menu.clearHistory')}
            >
              <IconTrash />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function MobileMenuDrawer({ open, onClose, onNavigate, onLogin, onOpenAccount, onLogout }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const { user } = useAuth();
  const { menuBanners } = useSiteContent();
  const [paneIndex, setPaneIndex] = useState(1);
  const [historyItems, setHistoryItems] = useState([]);
  const [tabIndicator, setTabIndicator] = useState({ x: 0, w: 0, ready: false });
  const panesRef = useRef(null);
  const tabsRef = useRef(null);
  const syncingRef = useRef(false);
  const {
    sheetRef,
    scrollRef,
    sheetStyle,
    overlayStyle,
    dragging,
    closing,
    dragHandlers,
  } = useBottomSheetGestures({
    open,
    onClose,
    handleOnly: false,
    scrollKey: paneIndex,
  });

  const scrollToPane = useCallback((index, behavior = 'smooth') => {
    const root = panesRef.current;
    if (!root) return;
    const width = root.clientWidth || 1;
    syncingRef.current = true;
    setPaneIndex(index);
    root.scrollTo({ left: width * index, behavior });
    window.setTimeout(() => { syncingRef.current = false; }, behavior === 'smooth' ? 320 : 0);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    setPaneIndex(1);
    const root = panesRef.current;
    if (!root) return;
    root.scrollLeft = root.clientWidth || 0;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setHistoryItems(readBrowseHistory());
    lockScroll();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      unlockScroll();
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !panesRef.current) return;
    const pane = panesRef.current.children[paneIndex];
    scrollRef.current = pane?.querySelector('.mobile-menu-sheet__pane-scroll') || pane;
  }, [open, paneIndex, scrollRef]);

  const updateTabIndicator = useCallback(() => {
    const root = tabsRef.current;
    if (!root) return;
    const active = root.querySelector('.mobile-menu-sheet__tab.is-active');
    if (!active) return;
    const rootRect = root.getBoundingClientRect();
    const target = active.querySelector('.mobile-menu-sheet__tab-label')
      || active.querySelector('.mobile-menu-sheet__tab-icon')
      || active;
    const rect = target.getBoundingClientRect();
    setTabIndicator({
      x: Math.round(rect.left - rootRect.left + root.scrollLeft),
      w: Math.round(rect.width),
      ready: true,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateTabIndicator();
    const onResize = () => updateTabIndicator();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, paneIndex, updateTabIndicator]);

  const onPanesScroll = () => {
    if (syncingRef.current) return;
    const root = panesRef.current;
    if (!root) return;
    const width = root.clientWidth || 1;
    const next = Math.round(root.scrollLeft / width);
    if (next !== paneIndex && next >= 0 && next < PANE_IDS.length) {
      setPaneIndex(next);
      if (next === 0) setHistoryItems(readBrowseHistory());
    }
  };

  if (!open) return null;

  const banners = menuBanners || [];

  const openBanner = (banner) => {
    onClose();
    onNavigate(banner.linkPage || 'promos');
    if (banner?.promoId) {
      const url = new URL(window.location.href);
      url.searchParams.set('promoId', String(banner.promoId));
      window.history.replaceState({}, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const openPromos = () => {
    onClose();
    onNavigate('promos');
  };

  const openHistoryItem = (item) => {
    onClose();
    onNavigate(item.pageId, item.opts || {});
  };

  const handleClearHistory = () => {
    clearBrowseHistory();
    setHistoryItems([]);
  };

  const openChat = () => {
    onClose();
    window.dispatchEvent(new CustomEvent('vb-support-open'));
  };

  return createPortal(
    <div
      className={`mobile-menu-sheet__overlay${closing ? ' is-closing' : ''}`}
      style={overlayStyle}
      onClick={onClose}
    >
      <aside
        ref={sheetRef}
        className={`mobile-menu-sheet${dragging ? ' is-dragging' : ''}${closing ? ' is-closing' : ''}`}
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={tr('mobile.menu')}
        data-source="portal"
        {...dragHandlers}
      >
        <div className="mobile-menu-sheet__thumb" aria-hidden="true">
          <span />
        </div>
        <div className="mobile-menu-sheet__wrap">
          <div className="mobile-menu-sheet__header">
            <div className="mobile-menu-sheet__header-panel">
              <div className="mobile-menu-sheet__tabs" role="tablist" ref={tabsRef}>
                <HeaderTab
                  active={paneIndex === 0}
                  iconOnly
                  icon={<IconHistory />}
                  ariaLabel={tr('mobile.menu.history')}
                  ariaSelected={paneIndex === 0}
                  onClick={() => {
                    if (paneIndex === 0) scrollToPane(1);
                    else {
                      setHistoryItems(readBrowseHistory());
                      scrollToPane(0);
                    }
                  }}
                />
                {MENU_TABS.map((item) => (
                  <HeaderTab
                    key={item.id}
                    active={paneIndex === item.pane}
                    label={tr(item.labelKey)}
                    ariaSelected={paneIndex === item.pane}
                    onClick={() => scrollToPane(item.pane)}
                  />
                ))}
                <span
                  className={`mobile-menu-sheet__tab-indicator${tabIndicator.ready ? ' is-ready' : ''}`}
                  style={{
                    width: `${tabIndicator.w}px`,
                    transform: `translateX(${tabIndicator.x}px)`,
                  }}
                  aria-hidden="true"
                />
              </div>
            </div>
            <button type="button" className="mobile-menu-sheet__close" onClick={onClose} aria-label="Close">
              <IconSheetClose />
            </button>
          </div>
          <div className="mobile-menu-sheet__body">
            <div
              className="mobile-menu-sheet__panes"
              ref={panesRef}
              onScroll={onPanesScroll}
              data-swipe-horizontal="true"
            >
              <div className="mobile-menu-sheet__pane">
                <HistoryBody
                  t={tr}
                  items={historyItems}
                  onOpen={openHistoryItem}
                  onClear={handleClearHistory}
                />
              </div>
              <div className="mobile-menu-sheet__pane">
                {user ? (
                  <MenuUserBody
                    t={tr}
                    user={user}
                    banners={banners}
                    onNavigate={onNavigate}
                    onClose={onClose}
                    onBanner={openBanner}
                    onPromos={openPromos}
                    onOpenAccount={onOpenAccount}
                    onLogout={onLogout}
                    onOpenChat={openChat}
                  />
                ) : (
                  <MenuGuestBody
                    t={tr}
                    banners={banners}
                    onNavigate={onNavigate}
                    onClose={onClose}
                    onLogin={onLogin}
                    onBanner={openBanner}
                    onPromos={openPromos}
                    onOpenChat={openChat}
                  />
                )}
              </div>
              <div className="mobile-menu-sheet__pane">
                <SportTabBody
                  t={tr}
                  locale={locale}
                  onNavigate={onNavigate}
                  onClose={onClose}
                  enabled={open && paneIndex === 2}
                />
              </div>
              <div className="mobile-menu-sheet__pane">
                <CasinoTabBody
                  t={tr}
                  locale={locale}
                  banners={banners}
                  onNavigate={onNavigate}
                  onClose={onClose}
                  onBanner={openBanner}
                  onPromos={openPromos}
                  enabled={open && paneIndex === 3}
                />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
}
